import React, { useState, useEffect, useRef } from "react";
import { Typography, Input, Button, Space, message } from "antd";
import { getBilling } from "../../ducks/action";
import ListCard from "../../../../molecules/ListCard";
import { SearchOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";

const ActionColumn = ({ text, record }) => {
  const history = useHistory();
  
  const handleViewInvoice = (e) => {
    e.preventDefault();
    // Open in new tab
    window.open(`/billing/invoice/${record.invoice_uid}`, '_blank');
  };

  return (
    <a href="#" onClick={handleViewInvoice}>
      View Invoice
    </a>
  );
};

const renderActionColumn = (text, record) => {
  return <ActionColumn text={text} record={record} />;
};

const renderStatusColumn = (text, record) => {
  if (record.status) {
    const capitalizedStatus =
      record.status.charAt(0).toUpperCase() + record.status.slice(1);

    return <Typography>{capitalizedStatus}</Typography>;
  }
  return null;
};

const getColumnSearchProps = (dataIndex) => {
  return {
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => {
      const searchInput = useRef(null);
      return (
        <div
          style={{
            padding: 8,
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <Input
            ref={searchInput}
            placeholder={`Search ${dataIndex}`}
            value={selectedKeys[0]}
            onChange={(e) =>
              setSelectedKeys(e.target.value ? [e.target.value] : [])
            }
            onPressEnter={() => {
              confirm();
              close();
            }}
            style={{
              marginBottom: 8,
              display: "block",
            }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => {
                confirm();
                close();
              }}
              icon={<SearchOutlined />}
              size="small"
              style={{
                width: 90,
              }}
            >
              Search
            </Button>
            <Button
              onClick={() => {
                clearFilters();
                confirm();
              }}
              size="small"
              style={{
                width: 90,
              }}
            >
              Reset
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => {
                close();
              }}
            >
              close
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: (filtered) => (
      <SearchOutlined
        style={{
          color: filtered ? "#1890ff" : undefined,
        }}
      />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        ?.toLowerCase()
        ?.includes(value?.toLowerCase()),
  };
};

const columns = [
  {
    title: "Invoice #",
    dataIndex: "invoice_uid",
    key: "invoice_uid",
    sorter: false,
    sortDirections: ['ascend', 'descend'],
    ...getColumnSearchProps("invoice_uid"),
  },
  {
    title: "Amount",
    dataIndex: "total_amount",
    key: "total_amount",
    sorter: false,
    sortDirections: ['ascend', 'descend'],
    ...getColumnSearchProps("total_amount"),
  },
  {
    title: "Invoice month",
    dataIndex: "created_at",
    key: "created_at",
    sorter: false,
    sortDirections: ['ascend', 'descend'],
    ...getColumnSearchProps("created_at"),
  },
  {
    title: "Payment Date",
    dataIndex: "payment_date",
    key: "payment_date",
    sorter: false,
    sortDirections: ['ascend', 'descend'],
    ...getColumnSearchProps("payment_date"),
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: false,
    sortDirections: ['ascend', 'descend'],
    render: renderStatusColumn,
    ...getColumnSearchProps("status"),
  },
  {
    title: "Action",
    dataIndex: "action",
    render: renderActionColumn,
  },
];

const index = () => {
  const [data, setData] = useState({ data: [], total_records: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [sortInfo, setSortInfo] = useState({
    field: '',
    order: ''
  });

  const fetchData = (pageNo, sortField = '', sortOrder = '') => {
    setLoading(true);
    const skip = (pageNo - 1) * 10;

    // Convert sort order to API format
    const apiSortOrder = sortOrder === 'ascend' ? 'asc' : sortOrder === 'descend' ? 'desc' : '';
    
    // Log the API call parameters for debugging
    // console.log('Fetching data with:', {
    //   page: skip,
    //   sortField,
    //   sortOrder: apiSortOrder
    // });

    getBilling(skip, sortField, apiSortOrder)
      .then((response) => {
        // console.log('API Response:', response); // Debug log
        if (response?.data?.header?.code === 200) {
          setData({
            data: response?.data?.body?.data || [],
            total_records: response?.data?.body?.total_records || 0
          });
        } else {
          setData({ data: [], total_records: 0 });
          message.error(response?.data?.header?.message || "Failed to fetch data");
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error('API Error:', error); // Debug log
        setData({ data: [], total_records: 0 });
        message.error(error?.response?.data?.header?.message || "Something went wrong");
        setLoading(false);
      });
  };

  const onTableChange = (pagination, filters, sorter) => {
    const { current } = pagination;
    setPage(current);

    // Handle both single-field and multi-field sorting
    const sortField = sorter.field || '';
    const sortOrder = sorter.order || '';
    
    setSortInfo({ field: sortField, order: sortOrder });
    
    // Log sorting info for debugging
    // console.log('Sort change:', {
    //   field: sortField,
    //   order: sortOrder
    // });

    fetchData(current, sortField, sortOrder);
  };

  useEffect(() => {
    fetchData(1);
  }, []);

  return (
    <ListCard
      ListCol={columns}
      ListData={data.data}
      onChange={onTableChange}
      listClass="nospace-card"
      loading={loading}
      pagination={{
        total: data.total_records,
        current: page,
        showSizeChanger: false,
        pageSize: 10,
      }}
      sortDirections={['ascend', 'descend']}
    />
  );
};

export default index;